import { CategoryType } from '@rocksoft/shared';
import { CategoryTypeToEntity } from '../shared/types';

export interface Category {
  readonly categoryType: CategoryType;
  readonly categoryId: string;
  readonly parent: Category | undefined;
}

export class ConcreteCategory<T extends CategoryType> implements Category {
  readonly categoryType: T;
  readonly categoryId: string;
  readonly categoryHumanId: string;
  readonly categoryDisplayName: string;
  readonly parent: Category | undefined;
  readonly entity: CategoryTypeToEntity[T] | undefined;

  constructor(
    categoryType: T,
    categoryId: string,
    categoryHumanId: string,
    categoryDisplayName: string,
    entity: CategoryTypeToEntity[T] | undefined,
    inherited: Category | undefined
  ) {
    this.categoryType = categoryType;
    this.categoryId = categoryId;
    this.categoryHumanId = categoryHumanId;
    this.categoryDisplayName = categoryDisplayName;
    this.entity = entity;
    this.parent = inherited;
  }

  /**
   * Returns an array containing this category and all its inherited categories recursively.
   * The array will contain all parent/inherited categories until the inherited property is null or undefined.
   */
  getAncestors(): Category[] {
    return getAncestors<Category>(this);
  }

  /**
   * Creates a new ConcreteCategory instance with an updated entity while preserving all other properties.
   * This method preserves the class prototype, ensuring class methods remain available.
   * @param entity - The updated entity that matches this category's type
   * @returns A new ConcreteCategory instance with the updated entity
   */
  withUpdatedEntity(entity: CategoryTypeToEntity[T]): ConcreteCategory<T> {
    return new ConcreteCategory(
      this.categoryType,
      this.categoryId,
      this.categoryHumanId,
      this.categoryDisplayName,
      entity,
      this.parent
    );
  }
}
// Standalone function to get all categories from any Category object
export function getAncestors<T extends { parent: T | undefined }>(root: T): T[] {
  const ancestors: T[] = [];
  let current: T | undefined = root;
  while (current != undefined) {
    ancestors.push(current);
    current = current.parent;
  }
  return ancestors;
}
